<template>
  <section class="recommend container">
    <h2 class="section-title"><span>{{ title }}</span><i class="iconfont icon-xihuan heart-icon"></i></h2>
    <p v-if="sub" class="section-sub">{{ sub }}</p>
    <div class="rec-wrap">
      <div class="rec-track" ref="trackRef">
        <NuxtLink v-for="r in items" :key="r.id" :to="`/goods/${r.id}`" class="card">
          <ElImage :src="r.img" fit="cover" class="card-image" />
          <div class="card-body">
            <div class="name">{{ r.name }}</div>
            <div class="price">￥{{ r.price }}</div>
          </div>
        </NuxtLink>
      </div>
      <button class="rec-prev" v-show="canPrev" @click="scrollPrev">
        <i class="iconfont icon-fanhui"></i>
      </button>
      <button class="rec-next" v-show="canNext" @click="scrollNext">
        <i class="iconfont icon-jinru"></i>
      </button>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, nextTick } from "vue";
const props = withDefaults(
  defineProps<{
    items: { id: number; name: string; price: number; img: string }[];
    title?: string;
    sub?: string;
  }>(),
  { title: "猜你喜欢", sub: "基于您的品味精心臻选。这方天地，专为读懂您的偏好而设，愿在这些与之共鸣的雅致藏品中，助您邂逅那件命中注定的心仪之物" }
);
const trackRef = ref<HTMLDivElement | null>(null);
const canPrev = ref(false);
const canNext = ref(false);
const updateArrows = () => {
  const el = trackRef.value;
  if (!el) {
    canPrev.value = false;
    canNext.value = false;
    return;
  }
  const max = el.scrollWidth - el.clientWidth;
  const x = el.scrollLeft;
  canPrev.value = x > 0;
  canNext.value = x < max - 1;
};
const scrollNext = () => {
  const el = trackRef.value;
  if (!el) return;
  el.scrollBy({ left: el.clientWidth * 0.5, behavior: "smooth" });
  setTimeout(updateArrows, 200);
};
const scrollPrev = () => {
  const el = trackRef.value;
  if (!el) return;
  el.scrollBy({ left: -el.clientWidth * 0.5, behavior: "smooth" });
  setTimeout(updateArrows, 200);
};
onMounted(() => {
  const el = trackRef.value;
  if (!el) return;
  updateArrows();
  const onScroll = () => updateArrows();
  const onResize = () => updateArrows();
  el.addEventListener("scroll", onScroll);
  window.addEventListener("resize", onResize);
  onUnmounted(() => {
    el.removeEventListener("scroll", onScroll);
    window.removeEventListener("resize", onResize);
  });
});
watch(
  () => props.items,
  async () => {
    await nextTick();
    updateArrows();
  },
  { deep: true }
);
</script>

<style scoped>
.container {
  width: 100%;
  padding: 0 0.9rem;
}
.section-title {
  text-align: center;
  font-size: 0.36rem;
  color: #333537;
  margin: 0.4rem 0 0.4rem;
}
.heart-icon {
  color: #ff0000;
  font-size: 1em;
  margin-left: 0.08rem;
}
.section-sub {
  text-align: center;
  color: #535557;
  font-size: 0.18rem;
  margin-bottom: 1rem;
  line-height: 0.3rem;
}
.rec-wrap {
  position: relative;
}
.rec-track {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 25%;
  gap: 0.3rem;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding-bottom: 0.2rem;
}
.rec-track::-webkit-scrollbar {
  display: none;
}
.rec-prev {
  position: absolute;
  left: 0;
  top: 45%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  border: none;
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 50%;
  cursor: pointer;
}
.rec-next {
  position: absolute;
  right: 0;
  top: 45%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  border: none;
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 50%;
  cursor: pointer;
}
.card {
  background: transparent;
  box-shadow: none;
  text-decoration: none;
  display: block;
}
.card-image {
  width: 100%;
  aspect-ratio: 2 / 3;
}
.card-body {
  text-align: center;
  padding: 0.4rem 0;
}
.card-body .name {
  font-size: 0.14rem;
  color: #424446;
  margin-bottom: 0.1rem;
}
.card-body .price {
  font-size: 0.16rem;
  color: #121212;
}
@media (max-width: 1280px) {
  .container {
    padding: 0 0.48rem;
  }
  .rec-track {
    grid-auto-columns: 33.33%;
  }
}
@media (max-width: 960px) {
  .container {
    padding: 0 0.24rem;
  }
  .rec-track {
    grid-auto-columns: 50%;
  }
}
@media (max-width: 640px) {
  .rec-track {
    grid-auto-columns: 100%;
  }
}
</style>
